import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useTranslation } from 'react-i18next';
import 'jspdf-autotable';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import {
  Layout,
  Card,
  Typography,
  Row,
  Col,
  Select,
  Input,
  Button,
  Checkbox,
  Divider,
  Descriptions,
  Tag,
  Spin,
  List,
  Statistic,
  Modal,
  message,
  Form,
  Result,
  Space,
  Tooltip
} from 'antd';
import {
  UserOutlined,
  BookOutlined,
  DollarOutlined,
  CalendarOutlined,
  DeleteOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import './Registrations.css';

const { Content } = Layout;
const { Title, Text } = Typography;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  return new Intl.DateTimeFormat('en-GB', options).format(date).toUpperCase();
};

const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  const [hours, minutes] = timeString.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

const formatNumber = (number) => {
  const parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    return '0.00';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(parsedNumber);
};

const Registrations = () => {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentDetails, setStudentDetails] = useState({});
  const [studentExistingClasses, setStudentExistingClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classPrices, setClassPrices] = useState({});
  
  const [promotion, setPromotion] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [priceBeforeVat, setPriceBeforeVat] = useState(0);
  const [vat, setVat] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [selectedClassSchedule, setSelectedClassSchedule] = useState([]);
  const [selectedClassDetails, setSelectedClassDetails] = useState({});
  const token = localStorage.getItem("token");
          let userRole = null;
          let userBranchId = null;
      
          if (token) {
          try {
              const decoded = jwtDecode(token);
              userRole = decoded.role;
              // Support both formats just in case
              userBranchId = decoded.branchId || decoded.branch_id || null;
          } catch (err) {
              console.error("Invalid token", err);
          }
          }
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchStudentExistingClasses = useCallback(async (studentId) => {
  if (!studentId) {
    setStudentExistingClasses([]);
    return;
  }
  
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/registrations/student/${studentId}/classes`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    if (response.data.success) {
      setStudentExistingClasses(response.data.classes || []);
    }
  } catch (error) {
    console.error('Error fetching student existing classes:', error);
    // Don't show error to user as this is background validation
  }
}, [API_URL]);

  useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);

  const fetchStudents = useCallback(async (search = '') => {
  try {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    const params = { search };
    if (userRole === "superadmin" && selectedBranch) {
      params.branchId = selectedBranch;
    }

    const response = await axios.get(`${API_URL}/students/reg-students-select`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    });

    if (response.data.success) {
      setStudents(response.data.students);
    }
  } catch (error) {
    console.error('Error fetching students:', error);
    message.error('Failed to fetch students');
  } finally {
    setIsLoading(false);
  }
}, [API_URL, selectedBranch, userRole]);


const fetchClasses = useCallback(async () => {
  try {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    const params = {};
    if (userRole === "superadmin" && selectedBranch) {
      params.branchId = selectedBranch;
    }

    const response = await axios.get(`${API_URL}/class-groups/reg-class-select`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    });

    const classesData = Array.isArray(response.data) ? response.data : [];

    setClasses(classesData);
  } catch (error) {
    console.error('Error fetching classes:', error);
    message.error('Failed to fetch classes');
  } finally {
    setIsLoading(false);
  }
}, [API_URL, selectedBranch, userRole]);


useEffect(() => {
  if (userRole === "superadmin" && !selectedBranch) return;
  fetchStudents();
  fetchClasses();
}, [fetchStudents, fetchClasses, selectedBranch, userRole]);



  const classOptions = classes.map(cls => {
  const isAlreadyRegistered = studentExistingClasses.some(
    existingClass => existingClass.class_id === cls.groupid
  );
  const isCurrentlySelected = selectedClasses.some(
    selectedClass => selectedClass.groupid === cls.groupid
  );
  
  return {
    value: cls.groupid,
    label: `${cls.class_code} ${t('start_date')} ${formatDate(cls.start_date)}${isAlreadyRegistered ? ' (Already Registered)' : ''}`,
    disabled: isAlreadyRegistered || isCurrentlySelected
  };
});

  const handleStudentSelect = (value) => {
  setSelectedStudent(value);
  const selected = students.find(student => student.value === value);
  setStudentDetails(selected || {});
  
  // Fetch student's existing registrations
  fetchStudentExistingClasses(value);
  
  // Clear selected classes when student changes
  setSelectedClasses([]);
  setClassPrices({});
  calculateTotalPrice([], {}, promotion, vat);
};

  const handleClassAdd = (value) => {
  const classToAdd = classes.find(cls => cls.groupid === value);
  
  // Check if already registered
  const isAlreadyRegistered = studentExistingClasses.some(
    existingClass => existingClass.class_id === value
  );
  
  if (isAlreadyRegistered) {
    message.error('Student is already registered for this class');
    return;
  }
  
  // Check if already selected
  if (selectedClasses.some(cls => cls.groupid === value)) {
    message.warning('Class already selected');
    return;
  }
  
  if (classToAdd) {
    const newSelectedClasses = [...selectedClasses, classToAdd];
    setSelectedClasses(newSelectedClasses);
    
    // Update class prices
    const newClassPrices = { ...classPrices };
    newClassPrices[value] = parseFloat(classToAdd.price) || 0;
    setClassPrices(newClassPrices);
    
    calculateTotalPrice(newSelectedClasses, newClassPrices, promotion, vat);

  }
};
  const handleClassRemove = (classId) => {
    const newSelectedClasses = selectedClasses.filter(cls => cls.groupid !== classId);
    setSelectedClasses(newSelectedClasses);
    
    // Remove from class prices
    const newClassPrices = { ...classPrices };
    delete newClassPrices[classId];
    setClassPrices(newClassPrices);
    
    calculateTotalPrice(newSelectedClasses, newClassPrices, promotion, vat);

  };


const handlePromotionChange = (e) => {
  const promoValue = e.target.value;
  setPromotion(promoValue);
  calculateTotalPrice(selectedClasses, classPrices, promoValue, vat);
};

const handleVatChange = (checked) => {
  setVat(checked);
  calculateTotalPrice(selectedClasses, classPrices, promotion, checked);
};


const calculateTotalPrice = (classes, prices, promo, withVat) => {
  const totalClassPrice = Object.values(prices).reduce((sum, price) => sum + price, 0);
  const parsedPromotion = parseFloat(promo) || 0;
  const priceBeforeVatCalc = totalClassPrice - parsedPromotion;

  setPriceBeforeVat(priceBeforeVatCalc);

  let finalPrice = priceBeforeVatCalc;
  if (withVat) {
    finalPrice = finalPrice * 1.07;
  }

  setTotalPrice(finalPrice);
};



  const showScheduleModal = (classDetails) => {
    setSelectedClassSchedule(classDetails.classes || []);
    setSelectedClassDetails(classDetails);
    setScheduleModalVisible(true);
  };

  const showConfirmModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmRegistration = async () => {
    try {
      setIsLoading(true);

      const validPromotion = promotion ? parseFloat(promotion) : 0;
      if (isNaN(validPromotion)) {
        message.error("Invalid promotion value");
        return;
      }

      const validTotalPrice = totalPrice ? parseFloat(totalPrice) : 0;
      if (isNaN(validTotalPrice) || validTotalPrice < 0) {
        message.error("Invalid total price value");
        return;
      }


      const userData = JSON.parse(localStorage.getItem('userData'));
      const userId = userData ? userData.userId : null;

      // Prepare batch registration data
      const registrationData = {
        studentId: selectedStudent,
        classIds: selectedClasses.map(cls => cls.groupid),
        prices: selectedClasses.map(cls => classPrices[cls.groupid]),
        promotion: validPromotion,
        paymentMethod: 'system',
        paymentConfirmed: true,
        createdBy: userId,
        discount: validPromotion,
        vat: vat,
        ...(userRole === "superadmin" && { branch_id: selectedBranch })
      };

      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/registrations`, registrationData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setIsConfirmationModalOpen(false);
      setSuccessModalVisible(true);
      resetForm();
    } catch (error) {
      console.error('Error saving registration:', error);
      message.error('Failed to complete registration');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedStudent('');
    setStudentDetails({});
    setSelectedClasses([]);
    setClassPrices({});
    setPromotion('');
    setTotalPrice(0);
    setVat(false);
    setPriceBeforeVat(0);
  };



  const getTotalClassPrice = () => {
    return Object.values(classPrices).reduce((sum, price) => sum + price, 0);
  };

  return (
    <Layout>
      <Sidebar />
      <Layout>
        <Header />
        <Content style={{ padding: '24px', marginLeft: '75px', marginTop: '64px' }}>
          <Card>
            <Title level={2}>{t('registration.register_for_course')}</Title>
            <Divider />
                {userRole === "superadmin" && (
                  <Form.Item label={t('Select Branch')} required>
                    <Select
                      placeholder={t("Select Branch")}
                      style={{ width: 300 }}
                      value={selectedBranch}
                      onChange={(val) => {
                        setSelectedBranch(val);
                        setSelectedStudent('');
                        setStudentDetails({});
                        setSelectedClasses([]);
                        setClassPrices({});
                      }}
                    >
                      {branches.map(branch => (
                        <Select.Option key={branch.id} value={branch.id}>
                          {branch.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

            <Spin spinning={isLoading}>
              <Row gutter={[24, 24]}>
                {/* Left Column */}
                <Col xs={24} md={14}>
                  <Card title={t('student')} bordered={false}>
                    <Form.Item label={t('registration.select_student')} required>
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder={t('registration.search_or_select_student')}
                        optionFilterProp="label"
                        onChange={handleStudentSelect}
                        value={selectedStudent}
                        options={students}
                        filterOption={(input, option) =>
                          option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        onSearch={(value) => fetchStudents(value)}
                        notFoundContent={isLoading ? <Spin size="small" /> : "No students found"}
                      />
                    </Form.Item>

                    {selectedStudent && (
                      <Descriptions bordered size="small">
                        <Descriptions.Item label={t('name')} span={3}>
                          <UserOutlined /> {studentDetails.firstname || studentDetails.label || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('address')} span={3}>
                          {(studentDetails.contactInfo?.address || studentDetails.address || 'N/A')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('phone')} span={3}>
                          {(studentDetails.contactInfo?.phone || studentDetails.phone || 'N/A')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('email')} span={3}>
                          {(studentDetails.contactInfo?.email || studentDetails.email || 'N/A')}
                        </Descriptions.Item>
                      </Descriptions>
                    )}

                    <Divider />

                    <Form.Item label={t('Select Class(s) for Registration')} required>
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder={t('Add Classes')}
                        onChange={handleClassAdd}
                        value={undefined}
                        options={classOptions}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          option.label.toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>

                    {selectedClasses.length > 0 && (
  <>
    <Divider orientation="left">
      {t('Selected Classes')} ({selectedClasses.length})
    </Divider>

    <Row gutter={[16, 16]}>
      {selectedClasses.map((cls) => (
        <Col xs={24} sm={24} md={12} lg={8} key={cls.groupid}>
          <Card
            size="small"
            title={`${t('classcode')}: ${cls.class_code}`}
            extra={
              <Tooltip title={t('remove')}>
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={() => handleClassRemove(cls.groupid)}
                />
              </Tooltip>
            }
          >
            <p>
              <strong>{t('start_date')}:</strong> {formatDate(cls.start_date)}
            </p>
            <p>
              <strong>{t('price')}:</strong> {formatNumber(classPrices[cls.groupid] || 0)}
            </p>
            <Space>
              <Button
                icon={<InfoCircleOutlined />}
                size="small"
                onClick={() => showScheduleModal(cls)}
              >
                {t('view_schedule')}
              </Button>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  </>
)}


                    <Divider />
                  </Card>
                </Col>

                {/* Right Column */}
                <Col xs={24} md={10}>
                  <Card title={t('price')} bordered={false}>
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Statistic 
                          title={t('Total Class Price')}
                          value={getTotalClassPrice()}
                          precision={2}
                          prefix={<BookOutlined />}
                        />
                      </Col>
                      <Col span={24}>
                        <Form.Item label={t('registration.promotion_discount')}>
                          <Input
                            prefix={<DollarOutlined />}
                            placeholder="0.00"
                            value={promotion}
                            onChange={handlePromotionChange}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Statistic 
                          title={t('registration.price_before_vat')}
                          value={priceBeforeVat}
                          precision={2}
                        />
                      </Col>
                      <Col span={24}>
                        <Form.Item>
                          <Checkbox checked={vat} onChange={(e) => handleVatChange(e.target.checked)}>
                            {t('registration.vat')}
                          </Checkbox>
                          {vat && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              +{formatNumber(priceBeforeVat * 0.07)}
                            </Tag>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Statistic 
                          title={t('registration.total_price')}
                          value={totalPrice}
                          precision={2}
                          valueStyle={{ color: '#3f8600', fontWeight: 'bold', fontSize: '24px' }}
                        />
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Button 
                          type="primary" 
                          onClick={showConfirmModal}
                          disabled={!selectedStudent || selectedClasses.length === 0}
                          block
                          size="large"
                        >
                          {t('registration.confirm_registration')} ({selectedClasses.length} {selectedClasses.length === 1 ? 'class' : 'classes'})
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Spin>
          </Card>
        </Content>
      </Layout>

      {/* Schedule Modal */}
      <Modal
        title={`${t('registration.class_schedule')} - ${selectedClassDetails.class_code}`}
        open={scheduleModalVisible}
        onCancel={() => setScheduleModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setScheduleModalVisible(false)}>
            {t('close')}
          </Button>
        ]}
        width={800}
      >
        <List
          size="small"
          bordered
          dataSource={selectedClassSchedule}
          renderItem={(slot, index) => (
            <List.Item>
              <CalendarOutlined style={{ marginRight: 8 }} />
              <Text>{formatDate(slot.date)}</Text>
              <Text type="secondary" style={{ margin: '0 8px' }}>{slot.schedule_day}</Text>
              <Text>{formatTime(slot.schedule_time)}</Text>
              <Text type="secondary" style={{ margin: '0 8px' }}>({slot.schedule_hour})</Text>
              <Text strong>{slot.subject_name}</Text>
            </List.Item>
          )}
        />
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        title={t('registration.confirm_registration_details')}
        open={isConfirmationModalOpen}
        onOk={handleConfirmRegistration}
        onCancel={() => setIsConfirmationModalOpen(false)}
        confirmLoading={isLoading}
        width={700}
      >
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={t('registration.student')}>
            {studentDetails.firstname || studentDetails.label?.split(' ')[0] || ''} {studentDetails.lastname || (studentDetails.label?.split(' ').slice(1).join(' ')) || ''}
          </Descriptions.Item>
          
          <Descriptions.Item label={t('registration.classes')} span={3}>
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
    {selectedClasses && selectedClasses.length > 0 ? (
      selectedClasses.map((cls) => (
        <Card
          key={cls.groupid}
          size="small"
          title={cls.class_code}
          style={{ width: 280, border: '1px solid #f0f0f0' }}
        >
          <p style={{ marginBottom: 4 }}>
            <strong>{t('subject')}:</strong> {cls.subject_name || t('na')}
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>{t('price')}:</strong> {formatNumber(classPrices[cls.groupid] || 0)}
          </p>
        </Card>
      ))
    ) : (
      <Text type="secondary">{t('noClassesFound')}</Text>
    )}
  </div>
</Descriptions.Item>



          <Descriptions.Item label={t('registration.total_class_price')}>
            {formatNumber(getTotalClassPrice())}
          </Descriptions.Item>

          {promotion && (
            <Descriptions.Item label={t('registration.promotion')}>
              -{formatNumber(promotion)}
            </Descriptions.Item>
          )}

          {vat && (
            <Descriptions.Item label={t('registration.vat')}>
              {formatNumber(priceBeforeVat * 0.07)}
            </Descriptions.Item>
          )}

          <Descriptions.Item label={t('registration.total_price')}>
            <Text strong style={{ fontSize: '16px', color: '#3f8600' }}>
              {formatNumber(totalPrice)}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={successModalVisible}
        onOk={() => setSuccessModalVisible(false)}
        onCancel={() => setSuccessModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setSuccessModalVisible(false)}>
            {t('ok')}
          </Button>
        ]}
      >
        <Result
          status="success"
          title={t('registration.successful_registration')}
        />
      </Modal>
    </Layout>
  );
};

export default withRoleAccess(Registrations, ['superadmin', 'admin']);